<template>
  <AppSidebar>
    <div class="space-y-10">
      <!-- Page Header -->
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-gray-100 dark:border-gray-800">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-2" style="font-family: Poppins, sans-serif">Order Portfolio</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">Manage and track client orders & project lifecycle</p>
        </div>
        <div class="flex flex-wrap gap-4" v-if="authStore.user?.role === 'admin'">
          <button 
            @click="exportSelected"
            v-if="selectedIds.length > 0"
            class="inline-flex items-center px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm text-[10px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            <ArrowDownTrayIcon class="-ml-1 mr-2 h-4 w-4" />
            Export Selected ({{ selectedIds.length }})
          </button>
          <button 
            @click="showCreateOrderModal = true; resetOrderForm();"
            class="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg shadow-primary-500/20 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 transition-all active:scale-95"
          >
            <PlusIcon class="-ml-1 mr-2 h-4 w-4" />
            Create Order
          </button>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <div class="md:col-span-2">
          <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">Search Portfolio</label>
          <div class="relative">
            <input 
              v-model="filters.search" 
              type="text" 
              placeholder="Order ID, Client name..." 
              class="block w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"
            />
          </div>
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">Order Status</label>
          <select 
            v-model="filters.status" 
            class="block w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none"
          >
            <option value="">All Statuses</option>
            <option v-for="status in orderStore.orderStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">Payment Status</label>
          <select 
            v-model="filters.payment_status" 
            class="block w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none"
          >
            <option value="">All Payments</option>
            <option v-for="status in orderStore.paymentStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">Account Manager</label>
          <select 
            v-model="filters.user_id" 
            class="block w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none"
          >
            <option value="">All Team</option>
            <option v-for="user in userStore.users" :key="user.id" :value="user.id">{{ user.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">Display Density</label>
          <div class="flex space-x-2">
            <select 
              v-model="filters.per_page" 
              class="block w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none"
            >
              <option value="15">15 Orders</option>
              <option value="100">100 Orders</option>
              <option value="300">300 Orders</option>
            </select>
            <button 
              @click="resetFilters" 
              class="p-3 bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-500 hover:text-primary-500 hover:border-primary-500/20 transition-all"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Order Statistics -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm transition-all group">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
            </div>
          </div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Total Orders</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ orderStats.total }}</p>
        </div>
        
        <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm transition-all group">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Pending</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ orderStats.pending }}</p>
        </div>
        
        <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm transition-all group">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
          </div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Processing</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ orderStats.processing }}</p>
        </div>
        
        <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm transition-all group">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Completed</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ orderStats.completed }}</p>
        </div>
        
        <div class="bg-white dark:bg-gray-800/50 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm transition-all group">
          <div class="flex items-center justify-between mb-4">
            <div class="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg class="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
              </svg>
            </div>
          </div>
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Net Revenue</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">${{ orderStats.revenue.toLocaleString() }}</p>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800/50 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
        <div class="p-8 sm:p-10 border-b border-gray-50 dark:border-gray-700/30">
          <h2 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest" style="font-family: Poppins, sans-serif">Recent Transactions</h2>
        </div>
        <div class="overflow-x-auto custom-scrollbar">
          <table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
            <thead class="bg-gray-50/50 dark:bg-gray-900/40">
              <tr>
                <th v-if="authStore.user?.role === 'admin'" class="px-8 py-5 text-left">
                  <input 
                    type="checkbox" 
                    :checked="selectedIds.length === orderStore.orders.length && orderStore.orders.length > 0"
                    @change="toggleSelectAll"
                    class="h-4 w-4 text-primary-600 border-gray-200 dark:border-gray-700 rounded focus:ring-0 focus:ring-offset-0 dark:bg-gray-800 transition-all" 
                  />
                </th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Order ID</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Client</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Amount</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Payment</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Assignee</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Date</th>
                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50 dark:divide-gray-800/50">
              <tr v-if="orderStore.loading">
                <td :colspan="authStore.user?.role === 'admin' ? 9 : 8" class="px-10 py-24 text-center">
                  <div class="flex flex-col items-center">
                    <div class="w-12 h-12 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-4"></div>
                    <p class="text-xs font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest">Synchronizing order data...</p>
                  </div>
                </td>
              </tr>
              
              <tr v-else-if="orderStore.orders.length === 0">
                <td :colspan="authStore.user?.role === 'admin' ? 9 : 8" class="px-10 py-24 text-center">
                  <div class="flex flex-col items-center">
                    <div class="w-20 h-20 bg-gray-50 dark:bg-gray-900/50 rounded-full flex items-center justify-center mb-6 border border-gray-100 dark:border-gray-800">
                      <svg class="w-10 h-10 text-gray-300 dark:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    </div>
                    <h3 class="text-sm font-bold text-gray-900 dark:text-white tracking-tight mb-2">No Orders Found</h3>
                    <p class="text-xs text-gray-400 dark:text-gray-600 font-medium mb-8">No matching orders found.</p>
                    <button @click="refreshOrders" class="px-6 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all">Refresh Orders</button>
                  </div>
                </td>
              </tr>
              
              <tr v-for="order in orderStore.orders" :key="order.id" class="group hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-all" :class="[selectedIds.includes(order.id) ? 'bg-primary-50/30 dark:bg-primary-900/5' : '']">
                <td class="px-8 py-6" v-if="authStore.user?.role === 'admin'">
                  <input 
                    type="checkbox" 
                    v-model="selectedIds" 
                    :value="order.id"
                    class="h-4 w-4 text-primary-600 border-gray-200 dark:border-gray-700 rounded focus:ring-0 focus:ring-offset-0 dark:bg-gray-800 transition-all" 
                  />
                </td>
                <td class="px-8 py-6">
                  <div class="text-xs font-bold text-primary-600 dark:text-primary-400 mb-0.5">{{ order.order_number }}</div>
                  <div class="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[150px] italic">{{ getOrderServices(order) }}</div>
                </td>
                <td class="px-8 py-6">
                  <div class="text-xs font-bold text-gray-900 dark:text-white tracking-tight">{{ getClientName(order) }}</div>
                </td>
                <td class="px-8 py-6 text-sm font-bold text-gray-900 dark:text-gray-200 tracking-tight">
                  ${{ order.total_amount?.toLocaleString() || '0' }}
                </td>
                <td class="px-8 py-6">
                  <select 
                    :value="order.status"
                    @change="updateOrderStatus(order, $event.target.value)"
                    :disabled="authStore.user?.role !== 'admin'"
                    class="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border-0 focus:ring-0 cursor-pointer transition-all hover:scale-105 dark:bg-gray-800 disabled:opacity-75 disabled:cursor-not-allowed"
                    :class="getStatusClass(order.status)"
                  >
                    <option v-for="status in orderStore.orderStatuses" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </td>
                <td class="px-8 py-6">
                  <select 
                    :value="order.payment_status || 'unpaid'"
                    @change="updatePaymentStatus(order, $event.target.value)"
                    :disabled="authStore.user?.role !== 'admin'"
                    class="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border-0 focus:ring-0 cursor-pointer transition-all hover:scale-105 dark:bg-gray-800 disabled:opacity-75 disabled:cursor-not-allowed"
                    :class="getPaymentStatusClass(order.payment_status)"
                  >
                    <option v-for="status in orderStore.paymentStatuses" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </td>
                <td class="px-8 py-6">
                  <div class="flex -space-x-2 overflow-hidden" v-if="order.developers?.length">
                    <div 
                      v-for="dev in order.developers" 
                      :key="dev.id"
                      class="h-7 w-7 rounded-lg ring-2 ring-white dark:ring-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-600 shadow-sm"
                      :title="dev.name"
                    >
                      <span class="text-[9px] font-black text-gray-600 dark:text-gray-400">{{ dev.name?.charAt(0) }}</span>
                    </div>
                  </div>
                  <span v-else class="text-[9px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest italic">Unassigned</span>
                </td>
                <td class="px-8 py-6 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                  {{ formatDate(order.created_at) }}
                </td>
                <td class="px-8 py-6">
                  <div class="flex items-center space-x-3 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                    <button @click="viewOrderDetails(order)" class="p-2 text-gray-400 hover:text-primary-500 transition-colors" title="View Details">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                    </button>
                    <button @click="generateInvoice(order)" class="p-2 text-gray-400 hover:text-green-500 transition-colors" title="Invoice">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    </button>
                    <button 
                      v-if="authStore.user?.role === 'admin'"
                      @click="editOrder(order)" 
                      class="p-2 text-gray-400 hover:text-blue-500 transition-colors" 
                      title="Edit"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Controls -->
        <div class="px-10 py-8 border-t border-gray-50 dark:border-gray-800/50 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
            Showing <span class="text-gray-900 dark:text-white">{{ (orderStore.pagination.current_page - 1) * orderStore.pagination.per_page + 1 }} - {{ Math.min(orderStore.pagination.current_page * orderStore.pagination.per_page, orderStore.pagination.total) }}</span> of <span class="text-gray-900 dark:text-white">{{ orderStore.pagination.total }}</span> orders
          </div>
          <div class="flex space-x-3">
            <button 
              @click="changePage(orderStore.pagination.current_page - 1)"
              :disabled="orderStore.pagination.current_page === 1"
              class="px-6 py-2.5 border border-gray-100 dark:border-gray-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            >
              Back
            </button>
            <button 
              @click="changePage(orderStore.pagination.current_page + 1)"
              :disabled="orderStore.pagination.current_page === orderStore.pagination.last_page"
              class="px-6 py-2.5 border border-gray-100 dark:border-gray-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Order Details Modal -->
    <div v-if="selectedOrder" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity" @click="closeOrderDetails"></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-[40px] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto custom-scrollbar border border-gray-100 dark:border-gray-700/50">
        <!-- Header -->
        <div class="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-10 py-8 border-b border-gray-50 dark:border-gray-700/30 flex items-center justify-between">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ selectedOrder.order_number }}</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">Order created on {{ formatDate(selectedOrder.created_at) }}</p>
          </div>
          <button @click="closeOrderDetails" class="p-3 bg-gray-50 dark:bg-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all">
            <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <div class="p-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <!-- Left Column: Order Info -->
          <div class="lg:col-span-2 space-y-10">
            <!-- Order Status & Summary -->
            <div class="flex flex-wrap items-center gap-8 p-8 bg-gray-50/50 dark:bg-gray-900/40 rounded-[32px] border border-gray-100 dark:border-gray-700/50">
              <div class="space-y-2">
                <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Order Status</p>
                <span class="inline-flex px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded-full" :class="getStatusClass(selectedOrder.status)">
                  {{ formatStatus(selectedOrder.status) }}
                </span>
              </div>
              <div class="space-y-2 ml-auto text-right">
                <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Total Amount</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">${{ selectedOrder.total_amount?.toLocaleString() }}</p>
              </div>
            </div>

            <!-- Items -->
            <section>
              <h4 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest" style="font-family: Poppins, sans-serif">Ordered Services</h4>
              <div class="space-y-4">
                <div v-for="item in selectedOrder.order_items" :key="item.id" 
                  class="flex items-center justify-between p-6 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-3xl shadow-sm transition-all hover:border-primary-500/20">
                  <div class="flex items-center space-x-5">
                    <div class="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center">
                      <svg class="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
                    </div>
                    <div>
                      <p class="text-sm font-bold text-gray-900 dark:text-white uppercase">{{ item.service?.name }}</p>
                      <p v-if="item.package" class="text-xs text-gray-500 dark:text-gray-400 font-medium">{{ item.package?.name }} Package</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-bold text-gray-900 dark:text-white uppercase">${{ item.price?.toLocaleString() }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 font-medium">Qty: {{ item.quantity }}</p>
                  </div>
                </div>
              </div>
            </section>

            <!-- Requirements/Description -->
            <section class="bg-blue-50/50 dark:bg-blue-900/10 p-8 rounded-[32px] border border-blue-100 dark:border-blue-900/30">
              <h4 class="text-xs font-bold text-blue-900 dark:text-blue-400 uppercase tracking-widest mb-4">Order Requirements</h4>
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {{ selectedOrder.requirements?.project_description || 'No specific requirements provided.' }}
              </p>
            </section>
            
            <!-- Communication Section -->
            <section>
              <h4 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-6">Communication History</h4>
              <div class="bg-gray-50/50 dark:bg-gray-900/30 rounded-[32px] border border-gray-100 dark:border-gray-700/50 p-6">
                <div class="space-y-6 mb-8 max-h-[300px] overflow-y-auto custom-scrollbar pr-4">
                  <div v-if="!selectedOrder.communication?.length" class="text-center py-12">
                    <p class="text-[10px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest italic">No messages recorded yet.</p>
                  </div>
                  <div v-else v-for="(msg, index) in selectedOrder.communication" :key="index" 
                    class="flex flex-col" :class="msg.author === 'admin' ? 'items-end' : 'items-start'">
                    <div class="max-w-[85%] p-5 rounded-[24px] text-sm font-medium shadow-sm transition-all" 
                      :class="msg.author === 'admin' ? 'bg-primary-600 text-white rounded-br-none' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-none'">
                      {{ msg.message }}
                    </div>
                    <span class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-2 px-1">{{ formatDate(msg.date) }}</span>
                  </div>
                </div>
                
                <div class="flex gap-3">
                  <input v-model="newMessage" type="text" placeholder="Transmit message to client..." 
                    class="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-6 py-4 text-sm font-medium focus:ring-2 focus:ring-primary-500/20 focus:outline-none dark:text-white transition-all"
                    @keyup.enter="sendMessage"
                  />
                  <button @click="sendMessage" class="p-4 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                  </button>
                </div>
              </div>
            </section>
          </div>

          <!-- Right Column: Sidebar Info -->
          <div class="space-y-8">
            <!-- Client Info Card -->
            <div class="p-8 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-[32px] shadow-sm">
              <h4 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6">Client Details</h4>
              <div class="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-50 dark:border-gray-700/30">
                <div class="w-14 h-14 bg-gray-50 dark:bg-gray-700 rounded-2xl flex items-center justify-center border border-gray-100 dark:border-gray-600">
                  <span class="text-xl font-bold text-gray-600 dark:text-gray-300 uppercase">{{ getClientName(selectedOrder).charAt(0) }}</span>
                </div>
                <div>
                  <p class="text-base font-bold text-gray-900 dark:text-white uppercase leading-tight">{{ getClientName(selectedOrder) }}</p>
                  <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">{{ selectedOrder.user?.email }}</p>
                </div>
              </div>
              <div class="space-y-4">
                <div class="flex items-start text-sm font-medium text-gray-600 dark:text-gray-400">
                  <svg class="w-4 h-4 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  <span class="text-[11px] leading-relaxed uppercase tracking-tight">{{ selectedOrder.user?.address || 'No location trace provided' }}</span>
                </div>
              </div>
            </div>

            <!-- Developer Assignment -->
            <div class="p-8 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-[32px] shadow-sm">
              <h4 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6">Assigned Team</h4>
              
              <div class="space-y-4 mb-8">
                <div v-if="!selectedOrder.developers?.length" class="text-center py-8 bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-gray-100 dark:border-gray-800/50">
                  <p class="text-[10px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest italic">No consultants assigned</p>
                </div>
                <div v-for="dev in selectedOrder.developers" :key="dev.id" 
                   class="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-700/50 group">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-primary-100/50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mr-4">
                      <span class="text-xs font-bold text-primary-600 dark:text-primary-400">{{ dev.name?.charAt(0) }}</span>
                    </div>
                    <div>
                      <p class="text-xs font-bold text-gray-900 dark:text-white uppercase">{{ dev.name }}</p>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{{ dev.role }}</p>
                    </div>
                  </div>
                  <button 
                    v-if="authStore.user?.role === 'admin'"
                    @click="removeDeveloper(dev.id)" 
                    class="p-2 text-gray-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>
              </div>

              <!-- Assign New Developer -->
              <div v-if="authStore.user?.role === 'admin'" class="pt-6 border-t border-gray-50 dark:border-gray-700/30">
                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">About Company</label>
                <div class="flex gap-2">
                  <select v-model="selectedDeveloperId" class="flex-1 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-xl px-4 py-2.5 text-xs font-bold dark:text-white focus:outline-none transition-all appearance-none">
                    <option value="">Select Team Member</option>
                    <option v-for="user in userStore.users.filter(u => ['admin', 'developer'].includes(u.role))" 
                      :key="user.id" :value="user.id">
                      {{ user.name }} ({{ user.role }})
                    </option>
                  </select>
                  <button @click="assignDeveloper" :disabled="!selectedDeveloperId" class="p-2.5 bg-gray-900 dark:bg-primary-600 text-white rounded-xl hover:scale-105 transition-all disabled:opacity-20 active:scale-95 shadow-lg shadow-black/10">
                    <PlusIcon class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Payment Status -->
            <div class="p-8 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-[32px] shadow-sm">
              <h4 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6">Payment Details</h4>
              <div class="space-y-4">
                <div class="flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/30 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/50">
                  <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tight">Payment Status</span>
                  <span class="px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest" :class="getPaymentStatusClass(selectedOrder.payment_status)">
                    {{ (selectedOrder.payment_status || 'unpaid') }}
                  </span>
                </div>
                <div class="flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/30 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/50 overflow-hidden">
                  <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tight">Transaction ID</span>
                  <span class="text-[10px] font-bold text-gray-900 dark:text-white uppercase tracking-tighter truncate ml-4">{{ selectedOrder.transaction_id || 'N/A' }}</span>
                </div>
                <button @click="generateInvoice(selectedOrder)" class="w-full mt-4 flex items-center justify-center px-6 py-4 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-[20px] text-[10px] font-black uppercase tracking-[0.2em] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm">
                  <ArrowDownTrayIcon class="w-4 h-4 mr-3" />
                  Download Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Order Modal -->
    <div v-if="editingOrder" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity" @click="editingOrder = null"></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-[40px] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto custom-scrollbar border border-gray-100 dark:border-gray-700/50">
        <!-- Header -->
        <div class="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-10 py-8 border-b border-gray-50 dark:border-gray-700/30 flex items-center justify-between">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight" style="font-family: Poppins, sans-serif">Modify Order: {{ editingOrder.order_number }}</h2>
          <button @click="editingOrder = null" class="p-3 bg-gray-50 dark:bg-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all">
            <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
        
        <form @submit.prevent="saveOrder" class="p-10 space-y-10">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
            <!-- Client Information -->
            <div class="space-y-6">
              <h3 class="text-[10px] font-black text-primary-500 uppercase tracking-[0.2em] border-b border-gray-50 dark:border-gray-700/30 pb-4">Subject Intel</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Client Name</label>
                  <input v-model="editingOrder.client_name" type="text" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Email Address</label>
                  <input v-model="editingOrder.client_email" type="email" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Location Trace</label>
                  <textarea v-model="editingOrder.client_address" rows="2" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"></textarea>
                </div>
              </div>
            </div>

            <!-- Invoice Information -->
            <div class="space-y-6">
              <h3 class="text-xs font-semibold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Invoice Details</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Invoice Number</label>
                  <input v-model="editingOrder.invoice_number" type="text" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Invoice Date</label>
                  <input v-model="editingOrder.invoice_date" type="date" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Technologies Used</label>
                  <input v-model="editingOrder.technology_used" type="text" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
              </div>
            </div>

            <!-- Financials -->
            <div class="space-y-6">
              <h3 class="text-xs font-semibold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Financial Overview</h3>
              <div class="grid grid-cols-2 gap-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Total Amount ($)</label>
                  <input v-model.number="editingOrder.total_price" type="number" step="0.01" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Discount ($)</label>
                  <input v-model.number="editingOrder.discount_amount" type="number" step="0.01" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Amount Paid ($)</label>
                  <input v-model.number="editingOrder.paid_amount" type="number" step="0.01" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Outstanding Balance ($)</label>
                  <input v-model.number="editingOrder.due_amount" type="number" step="0.01" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
              </div>
            </div>

            <!-- Status & Project -->
            <div class="space-y-6">
              <h3 class="text-xs font-semibold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Order Status</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Project Status</label>
                  <select v-model="editingOrder.status" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none">
                    <option v-for="status in orderStore.orderStatuses" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Payment Status</label>
                  <select v-model="editingOrder.payment_status" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none">
                    <option v-for="status in orderStore.paymentStatuses" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-6">
            <h3 class="text-xs font-semibold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Project Scope & Internal Notes</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Project Requirements</label>
                <textarea v-model="editingOrder.requirements.project_description" rows="4" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"></textarea>
              </div>
              <div>
                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Internal Team Notes</label>
                <textarea v-model="editingOrder.notes" rows="4" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"></textarea>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-4 pt-10 border-t border-gray-50 dark:border-gray-700/30">
            <button 
              type="button" 
              @click="editingOrder = null" 
              class="px-8 py-4 border border-gray-100 dark:border-gray-700 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              Abort
            </button>
            <button 
              type="submit" 
              :disabled="orderStore.loading"
              class="px-10 py-4 bg-primary-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-primary-700 transition-all shadow-xl shadow-primary-500/20 disabled:opacity-50 active:scale-95"
            >
              {{ orderStore.loading ? 'Saving Changes...' : 'Update Order' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Order Modal -->
    <div v-if="showCreateOrderModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity" @click="showCreateOrderModal = false"></div>
      <div class="relative bg-white dark:bg-gray-800 rounded-[40px] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto custom-scrollbar border border-gray-100 dark:border-gray-700/50">
        <!-- Header -->
        <div class="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-10 py-8 border-b border-gray-50 dark:border-gray-700/30 flex items-center justify-between">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight" style="font-family: Poppins, sans-serif">Initialize New Order</h2>
          <button @click="showCreateOrderModal = false" class="p-3 bg-gray-50 dark:bg-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all">
            <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <form @submit.prevent="saveNewOrder" class="p-10 space-y-10">
          <div class="bg-primary-50/50 dark:bg-primary-900/10 p-8 rounded-[32px] border border-primary-100 dark:border-primary-900/30">
            <label class="block text-[10px] font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest mb-4 px-1">Customer Selection</label>
            <div class="relative">
              <select 
                v-model="selectedClientId" 
                @change="handleClientSelect" 
                class="w-full bg-white dark:bg-gray-900/50 border border-primary-100 dark:border-primary-800/50 rounded-2xl py-3.5 px-6 text-sm font-bold dark:text-white focus:outline-none transition-all appearance-none"
              >
                <option value="">-- Manual Order Creation --</option>
                <option v-for="user in userStore.users" :key="user.id" :value="user.id">
                  {{ user.name }} ({{ user.email }})
                </option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div class="space-y-6">
              <h3 class="text-[10px] font-black text-primary-500 uppercase tracking-[0.2em] border-b border-gray-50 dark:border-gray-700/30 pb-4">Subject Intel</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Full Name*</label>
                  <input v-model="createForm.client_name" type="text" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Email Address*</label>
                  <input v-model="createForm.client_email" type="email" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
                <div>
                  <label class="block text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Location Trace</label>
                  <textarea v-model="createForm.client_address" rows="2" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"></textarea>
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <h3 class="text-xs font-semibold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Order Parameters</h3>
              <div class="space-y-6">
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Primary Service*</label>
                  <select v-model="createForm.service_id" @change="onServiceChange" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none">
                    <option v-for="service in serviceStore.services" :key="service.id" :value="service.id">
                      {{ service.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Service Tier (Package)</label>
                  <select v-model="createForm.package_id" @change="onPackageChange" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium appearance-none">
                    <option value="">No Tier Selected</option>
                    <option v-for="pkg in availablePackages" :key="pkg.id" :value="pkg.id">
                      {{ pkg.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Total Order Value ($)*</label>
                  <input v-model.number="createForm.total_price" type="number" step="0.01" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5 px-1">Project Requirements & Notes</label>
            <textarea v-model="createForm.description" rows="4" class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" placeholder="Project goals, deadlines, specific vision..."></textarea>
          </div>

          <div class="flex justify-end space-x-4 pt-10 border-t border-gray-50 dark:border-gray-700/30">
            <button 
              type="button" 
              @click="showCreateOrderModal = false" 
              class="px-8 py-4 border border-gray-100 dark:border-gray-700 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              Terminate
            </button>
            <button 
              type="submit" 
              :disabled="orderStore.loading"
              class="px-10 py-4 bg-primary-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-primary-700 transition-all shadow-xl shadow-primary-500/20 disabled:opacity-50 active:scale-95"
            >
              {{ orderStore.loading ? 'Creating Order...' : 'Create Order' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orders'
import { useServiceStore } from '@/stores/services'
import { useUserStore } from '@/stores/users'
import { useUIStore } from '@/stores/ui'
import { 
  PlusIcon, ArrowDownTrayIcon, XMarkIcon, 
  TrashIcon, PencilSquareIcon 
} from '@heroicons/vue/24/outline'
import axios from '@/utils/axios'
import AppSidebar from '@/components/layout/AppSidebar.vue'

import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const orderStore = useOrderStore()
const serviceStore = useServiceStore()
const userStore = useUserStore()
const uiStore = useUIStore()
const authStore = useAuthStore()

// State
const selectedOrder = ref(null)
const editingOrder = ref(null)
const newMessage = ref('')
const showAssignModal = ref(false)
const selectedDeveloperId = ref('')

// New State for enhancements
const showCreateOrderModal = ref(false)
const selectedClientId = ref('')
const selectedIds = ref([])
const availablePackages = ref([])

const filters = reactive({
  search: '',
  status: '',
  payment_status: '',
  user_id: '',
  from_date: '',
  to_date: '',
  per_page: '15',
  page: 1
})

const createForm = reactive({
  client_name: '',
  client_email: '',
  client_address: '',
  service_id: null,
  package_id: null,
  total_price: 0,
  description: '',
  user_id: null
})

// Initialize
onMounted(async () => {
  await refreshOrders()
  
  if (authStore.user?.role === 'admin') {
    await serviceStore.fetchAdminServices()
    await userStore.initializeUsers()
  } else {
    await serviceStore.fetchServices()
  }
})

// Methods
const refreshOrders = async () => {
  await orderStore.fetchOrders({ ...filters })
}

// Watchers
watch(filters, () => {
  refreshOrders()
}, { deep: true })

// Computed
// We now use orderStore.orders directly as the backend handles filtering

const orderStats = computed(() => {
  const orders = orderStore.orders
  return {
    total: orders.length,
    pending: orders.filter(order => ['draft', 'pending'].includes(order.status)).length,
    processing: orders.filter(order => ['processing', 'approved'].includes(order.status)).length,
    completed: orders.filter(order => order.status === 'completed').length,
    revenue: orders
      .filter(order => order.status === 'completed')
      .reduce((sum, order) => sum + (order.total_amount || 0), 0)
  }
})

const pendingOrders = computed(() => 
  orderStore.orders.filter(order => ['draft', 'pending'].includes(order.status))
)

const inProgressOrders = computed(() => 
  orderStore.orders.filter(order => ['processing', 'approved'].includes(order.status))
)

const completedOrders = computed(() => 
  orderStore.orders.filter(order => order.status === 'completed')
)
// Methods
const getClientName = (order) => {
  return order.user?.name || 'Unknown Client'
}

const getOrderServices = (order) => {
  if (order.order_items && order.order_items.length > 0) {
    return order.order_items.map(item => item.service?.name || 'Unknown Service').join(', ')
  }
  return 'No services'
}

const getServiceName = (serviceId) => {
  const service = serviceStore.getServiceById(serviceId)
  return service?.name || 'Unknown Service'
}

const updateOrderStatus = async (order, newStatus) => {
  try {
    await orderStore.updateOrderStatus(order.id, newStatus)
    uiStore.showNotification({
      type: 'success',
      title: 'Status Updated',
      message: `Order ${order.order_number} status updated to ${newStatus}`
    })
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Update Failed',
      message: error.message || 'Failed to update order status'
    })
  }
}

const viewOrderDetails = (order) => {
  selectedOrder.value = { ...order }
}

const editOrder = (order) => {
  editingOrder.value = JSON.parse(JSON.stringify(order))
  if (!editingOrder.value.requirements) {
    editingOrder.value.requirements = { project_description: '' }
  } else if (!editingOrder.value.requirements.project_description) {
    editingOrder.value.requirements.project_description = ''
  }
}

const saveOrder = async () => {
  if (!editingOrder.value) return
  
  try {
    const response = await orderStore.updateOrder(editingOrder.value.id, editingOrder.value)
    if (response) {
      editingOrder.value = null
      await refreshOrders()
    }
  } catch (error) {
    console.error('Failed to save order:', error)
  }
}

const generateInvoice = async (order) => {
  try {
    await orderStore.downloadInvoice(order.id)
  } catch (error) {
    console.error('Failed to download invoice:', error)
  }
}

const closeOrderDetails = () => {
  selectedOrder.value = null
}

const sendMessage = async () => {
  if (!newMessage.value.trim() || !selectedOrder.value) return
  
  try {
    // Add message to order communication
    const message = {
      author: 'admin',
      message: newMessage.value,
      date: new Date().toISOString()
    }
    
    // This would typically be an API call
    if (!selectedOrder.value.communication) {
      selectedOrder.value.communication = []
    }
    selectedOrder.value.communication.push(message)
    
    newMessage.value = ''
    
    uiStore.showNotification({
      type: 'success',
      title: 'Message Sent',
      message: 'Your message has been sent to the client'
    })
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Send Failed',
      message: 'Failed to send message'
    })
  }
}

const getStatusClass = (status) => {
  const statusObj = orderStore.getOrderStatusByValue(status)
  return statusObj?.bgClass || 'bg-gray-100 text-gray-800'
}

const getPaymentStatusClass = (status) => {
  const statusObj = orderStore.getPaymentStatusByValue(status || 'unpaid')
  return statusObj?.bgClass || 'bg-gray-100 text-gray-800'
}

const updatePaymentStatus = async (order, newStatus) => {
  try {
    await orderStore.updatePaymentStatus(order.id, newStatus)
    await refreshOrders()
  } catch (error) {
    console.error('Failed to update payment status:', error)
  }
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatStatus = (status) => {
  const statusLabels = {
    draft: 'Draft',
    pending: 'Pending Review',
    quote_sent: 'Quote Sent',
    approved: 'Approved',
    processing: 'In Progress',
    review: 'Under Review',
    completed: 'Completed',
    cancelled: 'Cancelled'
  }
  return statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)
}

const resetFilters = () => {
  Object.assign(filters, {
    search: '',
    status: '',
    payment_status: '',
    user_id: '',
    from_date: '',
    to_date: '',
    per_page: '15',
    page: 1
  })
}

const changePage = (page) => {
  filters.page = page
}

const toggleSelectAll = () => {
  if (selectedIds.value.length === orderStore.orders.length) {
    selectedIds.value = []
  } else {
    selectedIds.value = orderStore.orders.map(o => o.id)
  }
}

const exportSelected = async () => {
  await orderStore.exportOrdersCsv({ ids: selectedIds.value })
}

const handleClientSelect = () => {
  if (!selectedClientId.value) {
    resetOrderForm()
    return
  }
  const user = userStore.getUserById(parseInt(selectedClientId.value))
  if (user) {
    createForm.client_name = user.name
    createForm.client_email = user.email
    createForm.client_address = user.address || ''
    createForm.user_id = user.id
  }
}

const onServiceChange = () => {
  const service = serviceStore.services.find(s => s.id === createForm.service_id)
  if (service) {
    availablePackages.value = service.packages || []
    createForm.package_id = null
    createForm.total_price = service.starting_price || 0
  }
}

const onPackageChange = () => {
  const pkg = availablePackages.value.find(p => p.id === createForm.package_id)
  if (pkg) {
    createForm.total_price = pkg.price || 0
  }
}

const resetOrderForm = () => {
  Object.assign(createForm, {
    client_name: '',
    client_email: '',
    client_address: '',
    service_id: null,
    package_id: null,
    total_price: 0,
    description: '',
    user_id: null
  })
  selectedClientId.value = ''
  availablePackages.value = []
}

const saveNewOrder = async () => {
  try {
    const response = await orderStore.createOrder({
      ...createForm,
      status: 'pending',
      services: [{
        service_id: createForm.service_id,
        package_id: createForm.package_id,
        quantity: 1
      }]
    })
    
    if (response) {
      showCreateOrderModal.value = false
      resetOrderForm()
      await refreshOrders()
      uiStore.showNotification({
        type: 'success',
        title: 'Order Created',
        message: 'The new order has been successfully created.'
      })
    }
  } catch (error) {
    console.error('Failed to create order:', error)
  }
}

const assignDeveloper = async () => {
  if (!selectedDeveloperId.value || !selectedOrder.value) return
  
  try {
    uiStore.setLoading(true)
    const response = await axios.post(`orders/${selectedOrder.value.id}/assign-developer`, {
      user_id: selectedDeveloperId.value
    })
    
    if (response.data.success) {
      // Update local order data
      if (!selectedOrder.value.developers) {
        selectedOrder.value.developers = []
      }
      const developer = userStore.getUserById(selectedDeveloperId.value)
      selectedOrder.value.developers.push(developer)
      
      uiStore.showNotification({
        type: 'success',
        title: 'Developer Assigned',
        message: 'Developer has been assigned to this order successfully.'
      })
      selectedDeveloperId.value = ''
    }
  } catch (error) {
    console.error('Failed to assign developer:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Assignment Failed',
      message: error.response?.data?.message || 'Failed to assign developer'
    })
  } finally {
    uiStore.setLoading(false)
  }
}

const removeDeveloper = async (developerId) => {
  if (!selectedOrder.value) return
  
  if (confirm('Are you sure you want to remove this developer from the order?')) {
    try {
      uiStore.setLoading(true)
      const response = await axios.post(`orders/${selectedOrder.value.id}/remove-developer`, {
        user_id: developerId
      })
      
      if (response.data.success) {
        selectedOrder.value.developers = selectedOrder.value.developers.filter(d => d.id !== developerId)
        uiStore.showNotification({
          type: 'success',
          title: 'Developer Removed',
          message: 'Developer has been removed from this order.'
        })
      }
    } catch (error) {
      console.error('Failed to remove developer:', error)
    } finally {
      uiStore.setLoading(false)
    }
  }
}
</script>
